import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  Scissors,
  Package,
  Boxes,
  Truck,
  Wallet,
  Receipt,
  MessageCircle,
  UserCog,
  Percent,
  Banknote,
  Plug,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  AlertCircle,
  ClipboardCheck,
  Crown,
  Send,
  FolderOpen,
  BookOpen,
  History,
  Megaphone,
  Radar,
  Zap,
  Target,
  ShieldCheck,
  Archive,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import UnitSelector from "@/components/UnitSelector";
import AssistantWidget from "@/components/AssistantWidget";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", end: true },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testId: "nav-agenda" },
  { to: "/comandas", label: "Comandas", icon: Receipt, testId: "nav-comandas", hideFor: ["profissional"] },
  { to: "/pendentes", label: "Pendentes", icon: AlertCircle, testId: "nav-pending", restrict: "limitado", hideFor: ["profissional"] },
  { to: "/tarefas", label: "Tarefas", icon: ClipboardCheck, testId: "nav-tasks", badge: "tasks" },
  { to: "/clientes", label: "Clientes", icon: Users, testId: "nav-clients", hideFor: ["profissional"] },
  { to: "/funcionarios", label: "Funcionários", icon: UserCog, testId: "nav-employees", restrict: "limitado", hideFor: ["recepcao", "caixa", "profissional"] },
  { to: "/servicos", label: "Serviços", icon: Scissors, testId: "nav-services", restrict: "limitado", hideFor: ["caixa", "profissional"] },
  { to: "/produtos", label: "Produtos", icon: Package, testId: "nav-products", restrict: "limitado", hideFor: ["caixa", "profissional"] },
  { to: "/estoque", label: "Estoque", icon: Boxes, testId: "nav-stock", restrict: "limitado", hideFor: ["caixa", "profissional"] },
  { to: "/fornecedores", label: "Fornecedores", icon: Truck, testId: "nav-suppliers", restrict: "limitado", hideFor: ["recepcao", "caixa", "profissional"] },
  { to: "/financeiro", label: "Financeiro", icon: Wallet, testId: "nav-finance", restrict: "limitado", hideFor: ["recepcao", "caixa", "profissional"] },
  { to: "/comissoes", label: "Comissões", icon: Percent, testId: "nav-commissions", hideFor: ["recepcao", "caixa"] },
  { to: "/vales-pagamentos", label: "Vales & Pgto", icon: Banknote, testId: "nav-employee-payments", restrict: "limitado", hideFor: ["recepcao", "caixa", "profissional"] },
  { to: "/cobrancas", label: "Cobranças", icon: Receipt, testId: "nav-invoices", restrict: "limitado", hideFor: ["recepcao", "caixa", "profissional"] },
  { to: "/redes-sociais", label: "Redes Sociais", icon: MessageCircle, testId: "nav-social", restrict: "limitado", hideFor: ["caixa", "profissional"] },
  { to: "/mensagens", label: "Mensagens", icon: Send, testId: "nav-messages", restrict: "limitado", hideFor: ["caixa", "profissional"] },
  { to: "/arquivos", label: "Arquivos", icon: FolderOpen, testId: "nav-files", restrict: "limitado", hideFor: ["caixa", "profissional"] },
  { to: "/gregory-ai", label: "Gregory AI", icon: BookOpen, testId: "nav-gregory-ai", restrict: "limitado", hideFor: ["caixa", "profissional"] },
  { to: "/campanhas", label: "Campanhas", icon: Megaphone, testId: "nav-campaigns", hideFor: ["caixa", "profissional"] },
  { to: "/automacoes", label: "Automações", icon: Zap, testId: "nav-automations", restrict: "master" },
  { to: "/segmentos", label: "Segmentos", icon: Target, testId: "nav-segments", badge: "NOVO", hideFor: ["caixa", "profissional"] },
  { to: "/disparo-manual", label: "Disparo Manual (avançado)", icon: Send, testId: "nav-disparo-manual", restrict: "master" },
  { to: "/auditoria", label: "Auditoria", icon: History, testId: "nav-audit", restrict: "master" },
  { to: "/webhook-diagnostics", label: "Webhook Diagnostics", icon: Radar, testId: "nav-webhook-diagnostics", restrict: "master" },
  { to: "/pre-launch", label: "Pré-lançamento Z-API", icon: ShieldCheck, testId: "nav-pre-launch", restrict: "master" },
  { to: "/foundation/data-snapshots", label: "Data Snapshots", icon: Archive, testId: "nav-data-snapshots", restrict: "limitado", hideFor: ["recepcao", "caixa", "profissional"] },
  { to: "/integracoes", label: "Integrações", icon: Plug, testId: "nav-integrations", restrict: "limitado", hideFor: ["recepcao", "caixa", "profissional"] },
  { to: "/usuarios", label: "Usuários e Permissões", icon: Users, testId: "nav-users", restrict: "master" },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon, testId: "nav-settings", restrict: "limitado" },
];

export default function DashboardLayout() {
  const { user, logout, apiCall } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
  const [sidebarHover, setSidebarHover] = useState(false); // desktop hover preview
  const [sidebarPinned, setSidebarPinned] = useState(false); // desktop pin via button
  const [taskCount, setTaskCount] = useState(0);
  const [approveCount, setApproveCount] = useState(0);

  const isManager = user?.role === "master" || user?.team_role === "gerencia";
  const isDesktopVisible = sidebarHover || sidebarPinned;

  // Polling do contador
  useEffect(() => {
    let mounted = true;
    const fetchCount = async () => {
      try {
        const res = await apiCall("get", "/tasks/today/summary?scope=mine");
        if (mounted) setTaskCount(res.data?.total_pending || 0);
        if (isManager) {
          const a = await apiCall("get", "/tasks/today/summary?scope=to_approve");
          if (mounted) setApproveCount(a.data?.total_to_approve || 0);
        }
      } catch {
        /* silent */
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [apiCall, isManager]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const closeMobileMenu = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen flex liquid-bg relative overflow-hidden">
      {/* Liquid glass orbs (decorativos) */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(203,168,113,0.45) 0%, transparent 70%)" }} />
        <div className="absolute -bottom-40 -right-32 w-[28rem] h-[28rem] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(168,114,203,0.35) 0%, transparent 70%)" }} />
        <div className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full opacity-30" style={{ background: "radial-gradient(circle, rgba(113,168,203,0.4) 0%, transparent 70%)" }} />
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden backdrop-blur-sm"
          onClick={closeMobileMenu}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Desktop hover trigger (thin rail on left edge) */}
      <div
        className="hidden lg:block fixed left-0 top-0 h-screen w-3 z-30"
        onMouseEnter={() => setSidebarHover(true)}
        data-testid="sidebar-hover-rail"
      />

      {/* Sidebar — Liquid Glass (sempre flutuante) */}
      <aside
        className={`fixed top-0 left-0 h-screen w-64 z-40 transform transition-transform duration-300 p-3`}
        style={{
          transform: sidebarOpen
            ? "translateX(0)"
            : (typeof window !== "undefined" && window.innerWidth >= 1024 && isDesktopVisible)
              ? "translateX(0)"
              : "translateX(-100%)",
        }}
        onMouseEnter={() => setSidebarHover(true)}
        onMouseLeave={() => setSidebarHover(false)}
      >
        <div className="liquid-glass h-full flex flex-col rounded-2xl shadow-2xl">
          {/* Brand header */}
          <div className="p-4 flex items-center justify-between border-b border-white/10">
            <img src="/brand/logo.png" alt="Gregory João" className="h-20 w-auto drop-shadow-2xl" />
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10 rounded-full"
              onClick={() => {
                if (sidebarOpen) closeMobileMenu();
                else setSidebarPinned((p) => !p);
              }}
              title={sidebarPinned ? "Soltar (auto-esconder)" : "Fixar sidebar"}
              data-testid="sidebar-pin-button"
            >
              {sidebarPinned ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
            </Button>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto liquid-scroll">
            {navItems.filter((item) => {
              if (item.restrict === "limitado" && user?.team_role === "limitado") return false;
              if (item.restrict === "master" && user?.role !== "master") return false;
              // iter74: hideFor bloqueia menus por team_role (só espelho da autorização backend)
              if (user?.role !== "master" && Array.isArray(item.hideFor) && item.hideFor.includes(user?.team_role)) return false;
              return true;
            }).map((item) => {
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testId}
                  onClick={() => { closeMobileMenu(); if (!sidebarPinned) setSidebarHover(false); }}
                  className={({ isActive }) =>
                    `liquid-nav-item group flex items-center gap-3 px-3 py-2 text-sm rounded-xl transition-all duration-200 ${
                      isActive ? "liquid-nav-active" : "liquid-nav-idle"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
                  <span className="flex-1 font-medium tracking-tight">{item.label}</span>
                  {item.badge === "tasks" && taskCount > 0 && (
                    <span
                      className="liquid-badge text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center"
                      data-testid="tasks-badge"
                      title="Tarefas pendentes para você"
                    >
                      {taskCount > 9 ? "9+" : taskCount}
                    </span>
                  )}
                  {item.badge === "tasks" && approveCount > 0 && (
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center bg-orange-500 text-white shadow"
                      data-testid="approve-badge"
                      title="Aguardando sua aprovação"
                    >
                      {approveCount > 9 ? "9+" : approveCount}
                    </span>
                  )}
                  {typeof item.badge === "string" && item.badge !== "tasks" && (
                    <span
                      className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-widest bg-emerald-500 text-white"
                      title="Nova funcionalidade"
                    >
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* User card */}
          <div className="p-3 border-t border-white/10">
            <div className="liquid-user-card rounded-xl p-3 flex items-center gap-3 mb-2">
              <Avatar className="h-9 w-9 ring-1 ring-white/20">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-white/10 text-white text-sm font-medium">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-white flex items-center gap-1.5">
                  {user?.name}
                  {user?.role === "master" && (
                    <Crown className="h-3 w-3" strokeWidth={2} style={{ color: "#FBBF24" }} aria-label="Master" />
                  )}
                </div>
                <div className="text-[11px] text-white/60 truncate">{user?.email}</div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 rounded-xl"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content (sempre largura cheia — sidebar é flutuante) */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto min-w-0 relative z-10 w-full">
        {/* Top bar unificado */}
        <header className="sticky top-0 z-20 liquid-topbar px-3 sm:px-4 py-2 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (window.innerWidth >= 1024) setSidebarPinned((p) => !p);
              else setSidebarOpen(true);
            }}
            data-testid="sidebar-toggle-button"
            className="rounded-full"
            title="Abrir/fixar menu"
          >
            <Menu className="h-5 w-5" strokeWidth={1.5} />
          </Button>
          <img src="/brand/logo.png" alt="Gregory João" className="h-8 lg:h-10" />
          <div className="flex items-center gap-3">
            <UnitSelector />
          </div>
        </header>

        <div className="max-w-[1600px] mx-auto p-3 sm:p-4 lg:p-6">
          <Outlet />
        </div>
      </main>

      {/* Assistente IA flutuante (em todas as páginas autenticadas) */}
      <AssistantWidget />
    </div>
  );
}
